import { unbindElement } from "../brand/apply";
import type { BrandTokens } from "../brand/tokens";
import { boundingRect, elementBox, unionRects } from "../scene/geometry";
import { createId } from "../scene/ops";
import type { GroupElement, SceneElement } from "../scene/schema";
import {
  buildTemplateElements,
  type TemplateLogo,
  type TemplateNode,
  type TextMeasure,
} from "../templates/builder";
import { BRAND_STYLES, brandStyleTokens, getBrandStyle } from "../templates/styles";
import { clampToSchema, type PlacedElements } from "./saved";

/**
 * Building blocks ("Bausteine"): ready-made groups of elements – lower thirds,
 * cards, titles, calls to action, stickers, collages – that go into an
 * existing file with one click, in the colours and fonts of the brand. Like
 * library templates they are authored in brand roles on a reference scale
 * where the page's shorter side is 1080 px (docs/04-dokumentformat.md).
 */

export const BLOCK_CATEGORIES = [
  "bauchbinde",
  "titel",
  "intro",
  "social",
  "aufruf",
  "karte",
  "zahlen",
  "infografik",
  "zitat",
  "hinweis",
  "sticker",
  "rahmen",
  "collage",
  "mockup",
] as const;
export type BlockCategory = (typeof BLOCK_CATEGORIES)[number];

export const BLOCK_CATEGORY_LABELS: Record<BlockCategory, string> = {
  bauchbinde: "Bauchbinden",
  titel: "Titel",
  intro: "Intro & Outro",
  social: "Social",
  aufruf: "Aufrufe",
  karte: "Karten & Tipps",
  zahlen: "Zahlen & Fakten",
  infografik: "Infografiken",
  zitat: "Zitate & Sprechblasen",
  hinweis: "Hinweise",
  sticker: "Sticker",
  rahmen: "Rahmen & Overlays",
  collage: "Collagen",
  mockup: "Mockups",
};

/** Icons and charts are template nodes (templates/builder.ts); re-exported for the library. */
export type { ChartNode, IconNode } from "../templates/builder";

/** What blocks are made of: template nodes (incl. icons, charts and QR codes). */
export type BlockNode = TemplateNode;

/**
 * Where a block goes on the page. `fill` spans the whole page (collages);
 * the others keep clear of the app interface of reels at the top and bottom.
 */
export type BlockAnchor =
  | "top"
  | "center"
  | "bottom"
  | "lower-third"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "fill";

export interface LibraryBlock {
  id: string;
  title: string;
  category: BlockCategory;
  /** Size on the reference scale (ignored for `fill`: the page). */
  width: number;
  height: number;
  anchor: BlockAnchor;
  /** Video: how long it stays (ms). Node times are relative to the block's start. */
  duration?: number;
  /** Design style while there is no kit (`BRAND_STYLES`). */
  style?: string;
  /** Only offered in video files (motion) or only in designs. */
  only?: "video" | "design";
  /** Further search words (German synonyms), e.g. "Lower Third" or "Abo". */
  tags?: readonly string[];
  nodes: readonly BlockNode[] | ((area: { width: number; height: number }) => BlockNode[]);
}

/** Words a block is found by (title, category, tags). */
export function blockSearchTerms(block: LibraryBlock): string[] {
  return [block.title, BLOCK_CATEGORY_LABELS[block.category], ...(block.tags ?? [])];
}

export const DEFAULT_BLOCK_MS = 4000;

export interface BlockBuildOptions {
  /** Kit of the file: colours, fonts and logo, linked. Without it the style's values, unlinked. */
  tokens?: BrandTokens | null;
  style?: string | undefined;
  logos?: readonly TemplateLogo[];
  brandName?: string | undefined;
  measure?: TextMeasure | undefined;
  frame: { width: number; height: number };
  kind: "design" | "video";
  /** Video: start on the timeline (ms). */
  at?: number;
}

/** Tokens a block is built with (the kit, else its design style). */
export function blockTokens(
  block: LibraryBlock,
  options: Pick<BlockBuildOptions, "tokens" | "style">,
): BrandTokens {
  if (options.tokens) return options.tokens;
  const style =
    (options.style ? getBrandStyle(options.style) : undefined) ??
    (block.style ? getBrandStyle(block.style) : undefined) ??
    BRAND_STYLES[0]!;
  return brandStyleTokens(style);
}

/** The page area of a block on the reference scale (`fill`: the whole page). */
export function blockArea(
  block: LibraryBlock,
  frame: { width: number; height: number },
): { width: number; height: number } {
  if (block.anchor !== "fill") return { width: block.width, height: block.height };
  const s = Math.min(frame.width, frame.height) / 1080;
  return { width: frame.width / s, height: frame.height / s };
}

export function blockNodes(
  block: LibraryBlock,
  frame: { width: number; height: number },
): BlockNode[] {
  return typeof block.nodes === "function"
    ? block.nodes(blockArea(block, frame))
    : [...block.nodes];
}

/** Top-left corner of the block on the page (document px). */
function blockOrigin(
  anchor: BlockAnchor,
  frame: { width: number; height: number },
  size: { width: number; height: number },
): { x: number; y: number } {
  const W = frame.width;
  const H = frame.height;
  const short = Math.min(W, H);
  const margin = short * 0.055;
  const portrait = H > W * 1.2;
  const top = portrait ? H * 0.13 : margin;
  const bottom = H - size.height - (portrait ? H * 0.27 : margin);
  const centerX = (W - size.width) / 2;
  switch (anchor) {
    case "fill":
      return { x: 0, y: 0 };
    case "center":
      return { x: centerX, y: (H - size.height) / 2 };
    case "top":
      return { x: centerX, y: top };
    case "bottom":
      return { x: centerX, y: bottom };
    case "lower-third":
      return {
        x: margin,
        y: portrait ? H * 0.58 - size.height : H - size.height - margin * 1.6,
      };
    case "top-left":
      return { x: margin, y: top };
    case "top-right":
      return { x: W - size.width - margin, y: top };
    case "bottom-left":
      return { x: margin, y: bottom };
    case "bottom-right":
      return { x: W - size.width - margin, y: bottom };
  }
}

/**
 * The elements of a block for a page: scaled to the page (never wider than
 * it), placed by its anchor and grouped, so it moves as one. In videos it
 * appears at `at` for its duration; designs get no times or animations.
 */
export function buildBlock(block: LibraryBlock, options: BlockBuildOptions): PlacedElements {
  const { frame } = options;
  const area = blockArea(block, frame);
  const base = Math.min(frame.width, frame.height) / 1080;
  const s =
    block.anchor === "fill"
      ? base
      : Math.min(base, (frame.width * 0.94) / area.width, (frame.height * 0.94) / area.height);
  const linked = Boolean(options.tokens);
  const tokens = blockTokens(block, options);
  const ctx = {
    tokens,
    linked,
    logos: linked ? (options.logos ?? []) : [],
    brandName: options.brandName?.trim() || "Dein Logo",
    measure: options.measure,
  };
  const elements: SceneElement[] = buildTemplateElements(blockNodes(block, frame), ctx, s);
  const origin = blockOrigin(block.anchor, frame, {
    width: area.width * s,
    height: area.height * s,
  });
  const at = Math.max(0, Math.round(options.at ?? 0));
  const duration = block.duration ?? DEFAULT_BLOCK_MS;
  for (const element of elements) {
    element.x = Math.round((element.x + origin.x) * 10) / 10;
    element.y = Math.round((element.y + origin.y) * 10) / 10;
    clampToSchema(element);
    if (!linked) unbindElement(element);
    if (options.kind === "video") {
      const relative = element.time ?? { start: 0, end: duration };
      const start = Math.min(relative.start, duration - 100);
      element.time = {
        start: at + Math.max(0, Math.round(start)),
        end: at + Math.round(Math.min(duration, Math.max(start + 100, relative.end))),
      };
    } else {
      delete element.time;
      delete element.animation;
    }
  }
  if (elements.length === 1) {
    const [only] = elements as [SceneElement];
    if (!only.name) only.name = block.title;
    return { roots: [only], elements: [only] };
  }
  const bounds = unionRects(elements.map((element) => boundingRect(elementBox(element))))!;
  const group: GroupElement = {
    id: createId("group"),
    type: "group",
    name: block.title,
    x: Math.round(bounds.x * 10) / 10,
    y: Math.round(bounds.y * 10) / 10,
    width: Math.max(1, bounds.width),
    height: Math.max(1, bounds.height),
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    children: elements.map((element) => element.id),
    ...(options.kind === "video" ? { time: { start: at, end: at + duration } } : {}),
  };
  for (const element of elements) {
    element.x -= group.x;
    element.y -= group.y;
  }
  return { roots: [group], elements: [group, ...elements] };
}
